import React, { useEffect, useState } from "react";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";

import { GridColDef, GridInitialState } from "@mui/x-data-grid-premium";
import { BrandSummary_T } from "../../../types/Brand";
import { getDateValue, renderDateCell, renderNumberCell } from "utils/dataGrid";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";

interface BrandFillRatesBySourceTableProps {
  brand_id: string;
}

const defaultGridState: GridInitialState = {
  filter: {
    filterModel: {
      items: [],
    },
  },
  sorting: { sortModel: [{ field: "country_name", sort: "asc" }] },
  pagination: { paginationModel: { pageSize: 25, page: 0 } },
  pinnedColumns: { left: ["country_name", "country_code"] },
  columns: {
    columnVisibilityModel: {
      count_omni_name: false,
      count_omni_address: false,
      count_omni_coordinates: false,
      count_omni_business_category_id: false,
      count_omni_building_polygon: false,
      count_non_omni_name: false,
      count_non_omni_address: false,
      count_non_omni_coordinates: false,
      count_non_omni_business_category_id: false,
      count_non_omni_building_polygon: false,
    },
  },
  density: "compact",
  aggregation: {
    model: {},
  },
};

const columns_def: GridColDef[] = [
  {
    field: "country_name",
    headerName: "Country",
    description:
      "A country where this brand has physical locations, derived from the distinct countries of places with a chain_id for this brand.",
    type: "string",
    width: 200,
  },
  {
    field: "country_code",
    headerName: "Country Code",
    description: "ISO 3166-1 alpha-2 for this country.",
    type: "string",
    width: 100,
  },
  {
    field: "omni_poi_count",
    headerName: "Omni Active Count",
    description:
      "The number of places associated with this brand's chain_id that are not ignored and not duplicates.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "non_omni_poi_count",
    headerName: "Non-Omni Active Count",
    description:
      "The number of places associated with this brand's chain_id that do not have an external_id for an Omniscraper, are not ignored, and are not duplicates.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "fill_rate_omni_location_id",
    headerName: "Omni Location ID (%)",
    description:
      "The proportion of active places with an external_id for an Omniscraper associated with this brand and a non-null location_id value, calculated as the count of such places divided by the Omni Active Count.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "count_omni_name",
    headerName: "Omni Name (Count)",
    description:
      "The count of active places with an external_id for an Omniscraper associated with this brand and a non-null name value.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "fill_rate_omni_name",
    headerName: "Omni Name",
    description:
      "The proportion of active places with an external_id for an Omniscraper associated with this brand and a non-null name value, calculated as the count of such places divided by the Omni Active Count.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "count_omni_address",
    headerName: "Omni Address (Count)",
    description:
      "The count of active places with an external_id for an Omniscraper associated with this brand and a non-null address value.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "fill_rate_omni_address",
    headerName: "Omni Address",
    description:
      "The proportion of active places with an external_id for an Omniscraper associated with this brand and a non-null address value, calculated as the count of such places divided by the Omni Active Count.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "count_omni_coordinates",
    headerName: "Omni Coordinates (Count)",
    description:
      "The count of active places with an external_id for an Omniscraper associated with this brand and a non-null coordinates value.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "fill_rate_omni_coordinates",
    headerName: "Omni Coordinates",
    description:
      "The proportion of active places with an external_id for an Omniscraper associated with this brand and a non-null coordinates value, calculated as the count of such places divided by the Omni Active Count.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "count_omni_business_category",
    headerName: "Omni Business Category (Count)",
    description:
      "The count of active places with an external_id for an Omniscraper associated with this brand and a non-null business category value.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "fill_rate_omni_business_category",
    headerName: "Omni Business Category",
    description:
      "The proportion of active places with an external_id for an Omniscraper associated with this brand and a non-null business category value, calculated as the count of such places divided by the Omni Active Count.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "count_omni_building_polygon",
    headerName: "Omni Polygon (Count)",
    description:
      "The count of active places with an external_id for an Omniscraper associated with this brand and a non-null polygon value.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "fill_rate_omni_building_polygon",
    headerName: "Omni Polygon",
    description:
      "The proportion of active places with an external_id for an Omniscraper associated with this brand and a non-null polygon value, calculated as the count of such places divided by the Omni Active Count.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "fill_rate_non_omni_location_id",
    headerName: "Non Omni Location ID (%)",
    description:
      "The proportion of active places without an external_id for an Omniscraper associated with this brand that have a non-null location_id value, calculated as the count of such places divided by the Non-Omni Active Count.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "count_non_omni_name",
    headerName: "Non-Omni Name (Count)",
    description:
      "The count of active places without an external_id for an Omniscraper associated with this brand that have a non-null name value.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "fill_rate_non_omni_name",
    headerName: "Non-Omni Name",
    description:
      "The proportion of active places without an external_id for an Omniscraper associated with this brand that have a non-null name value, calculated as the count of such places divided by the Non-Omni Active Count.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "count_non_omni_address",
    headerName: "Non-Omni Address (Count)",
    description:
      "The count of active places without an external_id for an Omniscraper associated with this brand that have a non-null address value.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "fill_rate_non_omni_address",
    headerName: "Non-Omni Address",
    description:
      "The proportion of active places without an external_id for an Omniscraper associated with this brand that have a non-null address value, calculated as the count of such places divided by the Non-Omni Active Count.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "count_non_omni_business_category",
    headerName: "Non-Omni Business Category (Count)",
    description:
      "The count of active places without an external_id for an Omniscraper associated with this brand that have a non-null business category value.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "fill_rate_non_omni_business_category",
    headerName: "Non-Omni Business Category",
    description:
      "The proportion of active places without an external_id for an Omniscraper associated with this brand that have a non-null business category value, calculated as the count of such places divided by the Non-Omni Active Count.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "count_non_omni_building_polygon",
    headerName: "Non-Omni Polygon (Count)",
    description:
      "The count of active places without an external_id for an Omniscraper associated with this brand that have a non-null polygon value.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "fill_rate_non_omni_building_polygon",
    headerName: "Non-Omni Polygon",
    description:
      "The proportion of active places without an external_id for an Omniscraper associated with this brand that have a non-null polygon value, calculated as the count of such places divided by the Non-Omni Active Count.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "query_last_executed",
    headerName: "Data Last Updated",
    description:
      "The date when this data was retrieved and aggregated from the places table in the production database.",
    type: "date",
    width: 200,
    valueGetter: getDateValue,
    renderCell: renderDateCell,
  },
];

const BrandFillRatesBySourceTable: React.FC<
  BrandFillRatesBySourceTableProps
> = ({ brand_id }) => {
  const [brandFillRatesBySourceData, setBrandFillRatesBySourceData] = useState<
    BrandSummary_T[]
  >([]);
  const [loading, setLoading] = useState(true);

  const { showError } = useError();
  const { apiRequest } = useApi();

  useEffect(() => {
    if (brand_id) {
      const fetchBrandFillRatesData = async () => {
        try {
          const response = await apiRequest<BrandSummary_T[]>(
            `/brand_reports/brand_fill_rates_by_source/${brand_id}`,
            {
              method: "GET",
            }
          );
          if (response.data) {
            setBrandFillRatesBySourceData(response.data);
          }
        } catch (error) {
          console.error(error);
          showError(
            `Failed to fetch brand fill rate by source data for brand ID ${brand_id}.`
          );
        } finally {
          setLoading(false);
        }
      };
      fetchBrandFillRatesData();
    }
  }, [brand_id]);

  return (
    <GaneshDataGrid
      loading={loading}
      data={brandFillRatesBySourceData}
      columns={columns_def}
      gridId="brandFillRatesBySourceGrid"
      defaultGridState={defaultGridState}
    />
  );
};

export default BrandFillRatesBySourceTable;
